import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Image, Link, Mail, FileText, Copyright, Globe, Plus, Trash2 } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';

interface FooterConfig {
  id?: number;
  logo_url: string;
  site_name: string;
  site_desc: string;
  copyright: string;
  icp_info: string;
  contact_email: string;
  quick_links: { name: string; path: string }[];
  is_enabled: boolean;
}

const defaultConfig: FooterConfig = {
  logo_url: '',
  site_name: '编程优题库',
  site_desc: '专注青少年编程教育，助力科技特长生成长',
  copyright: '© 2024 编程优题库 版权所有',
  icp_info: '苏ICP备2026023699号',
  contact_email: 'kefu_51kc@foxmail.com',
  quick_links: [
    { name: '刷题平台', path: '/exam-categories' },
    { name: '帮助中心', path: '/help' },
    { name: '管理员登录', path: '/admin' },
  ],
  is_enabled: true,
};

export default function FooterSettings() {
  const [config, setConfig] = useState<FooterConfig>(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/footer-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get' }),
      });
      const result = await response.json();
      if (result.success && result.data) {
        setConfig({
          ...defaultConfig,
          ...result.data,
          quick_links: result.data.quick_links || defaultConfig.quick_links,
        });
      }
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/footer-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save', data: config }),
      });
      const result = await response.json();
      if (result.success) {
        setMessage('保存成功！');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('保存失败：' + result.error);
      }
    } catch (error: any) {
      setMessage('保存失败：' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `footer-logo-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('site-assets')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('site-assets')
        .getPublicUrl(fileName);

      setConfig({ ...config, logo_url: publicUrl });
      setMessage('Logo上传成功！');
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage('上传失败：' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const addQuickLink = () => {
    setConfig({
      ...config,
      quick_links: [...config.quick_links, { name: '', path: '' }],
    });
  };

  const removeQuickLink = (index: number) => {
    const newLinks = config.quick_links.filter((_, i) => i !== index);
    setConfig({ ...config, quick_links: newLinks });
  };

  const updateQuickLink = (index: number, field: 'name' | 'path', value: string) => {
    const newLinks = config.quick_links.map((link, i) =>
      i === index ? { ...link, [field]: value } : link
    );
    setConfig({ ...config, quick_links: newLinks });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">页脚设置</h1>
        <p className="text-gray-500 mt-1">配置网站页脚显示内容</p>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 p-4 rounded-lg ${message.includes('成功') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
        >
          {message}
        </motion.div>
      )}

      <div className="space-y-6">
        {/* Logo设置 */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5" />
            Logo设置
          </h2>
          <div className="flex items-center gap-4">
            {config.logo_url && (
              <img src={config.logo_url} alt="Logo" className="w-16 h-16 object-contain" />
            )}
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 cursor-pointer transition-colors"
              >
                {uploading ? '上传中...' : '更换Logo'}
              </label>
            </div>
          </div>
        </div>

        {/* 基本信息 */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            基本信息
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">网站名称</label>
              <input
                type="text"
                value={config.site_name}
                onChange={(e) => setConfig({ ...config, site_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">联系邮箱</label>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={config.contact_email}
                  onChange={(e) => setConfig({ ...config, contact_email: e.target.value })}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">网站描述</label>
              <textarea
                value={config.site_desc}
                onChange={(e) => setConfig({ ...config, site_desc: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* 快速链接 */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Link className="w-5 h-5" />
            快速链接
          </h2>
          <div className="space-y-3">
            {config.quick_links.map((link, index) => (
              <div key={index} className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="链接名称"
                  value={link.name}
                  onChange={(e) => updateQuickLink(index, 'name', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="链接地址"
                  value={link.path}
                  onChange={(e) => updateQuickLink(index, 'path', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => removeQuickLink(index)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              onClick={addQuickLink}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              添加链接
            </button>
          </div>
        </div>

        {/* 版权信息 */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Copyright className="w-5 h-5" />
            版权信息
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">版权信息</label>
              <input
                type="text"
                value={config.copyright}
                onChange={(e) => setConfig({ ...config, copyright: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                <Globe className="w-4 h-4" />
                ICP备案号
              </label>
              <input
                type="text"
                value={config.icp_info}
                onChange={(e) => setConfig({ ...config, icp_info: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* 保存按钮 */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            {saving ? '保存中...' : '保存设置'}
          </button>
        </div>
      </div>
    </div>
  );
}
